<template>
  <teleport to="body">
    <div v-if="modelValue" class="modal-overlay" @click.self="close">
      <div class="modal-content settings-modal">
        <div class="modal-header">
          <h2>Settings</h2>
          <button class="close-btn" @click="close">&times;</button>
        </div>

        <div class="modal-body">
          <!-- User Info Section (editable) -->
          <section class="settings-section">
            <h3>Profile</h3>
            <div class="profile-form">
              <div class="form-group">
                <label for="profile-name">Name</label>
                <input
                  id="profile-name"
                  v-model="profileForm.name"
                  type="text"
                  placeholder="Your name"
                  :disabled="profileSaving"
                />
              </div>
              
              <div class="form-group">
                <label for="profile-email">Email</label>
                <input
                  id="profile-email"
                  :value="userInfo.email"
                  type="email"
                  disabled
                  class="disabled-input"
                />
                <span class="field-hint">Email cannot be changed</span>
              </div>
              
              <div class="form-group">
                <label for="profile-phone">Phone</label>
                <input
                  id="profile-phone"
                  v-model="profileForm.phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  :disabled="profileSaving"
                />
                <span class="field-hint">For SMS notifications</span>
              </div>
              
              <div class="form-group">
                <label for="profile-timezone">Timezone</label>
                <select
                  id="profile-timezone"
                  v-model="profileForm.timezone"
                  :disabled="profileSaving"
                >
                  <option value="America/Los_Angeles">Pacific Time (LA)</option>
                  <option value="America/Denver">Mountain Time (Denver)</option>
                  <option value="America/Chicago">Central Time (Chicago)</option>
                  <option value="America/New_York">Eastern Time (NY)</option>
                  <option value="America/Phoenix">Arizona (Phoenix)</option>
                  <option value="America/Anchorage">Alaska</option>
                  <option value="Pacific/Honolulu">Hawaii</option>
                </select>
              </div>
              
              <button
                class="btn btn-primary save-profile-btn"
                @click="saveProfile"
                :disabled="profileSaving || !profileChanged"
              >
                {{ profileSaving ? 'Saving...' : 'Save Profile' }}
              </button>
            </div>
            
            <div v-if="userInfo.roles?.length" class="roles-display">
              <span class="roles-label">Roles:</span>
              <span class="roles-value">{{ userInfo.roles.join(', ') }}</span>
            </div>
          </section>

          <!-- Theme Section -->
          <section class="settings-section">
            <h3>Appearance</h3>
            <div class="setting-row">
              <div class="setting-info">
                <span class="setting-label">Dark Mode</span>
                <span class="setting-description">Use dark theme for the interface</span>
              </div>
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  v-model="isDarkModeLocal"
                  @change="toggleTheme"
                />
                <span class="toggle-slider"></span>
              </label>
            </div>
          </section>

          <!-- Notifications Section -->
          <section class="settings-section">
            <h3>Notifications</h3>
            <p class="section-description">
              Receive notifications for important updates
            </p>

            <div class="setting-row">
              <div class="setting-info">
                <span class="setting-label">Email Notifications</span>
                <span class="setting-description">Receive updates via email</span>
              </div>
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  v-model="preferences.email_notifications"
                  @change="savePreferences"
                />
                <span class="toggle-slider"></span>
              </label>
            </div>

            <div class="setting-row">
              <div class="setting-info">
                <span class="setting-label">SMS Notifications</span>
                <span class="setting-description">Receive urgent alerts via text</span>
              </div>
              <label class="toggle-switch">
                <input
                  type="checkbox"
                  v-model="preferences.sms_notifications"
                  @change="savePreferences"
                  :disabled="!profileForm.phone"
                />
                <span class="toggle-slider" :class="{ disabled: !profileForm.phone }"></span>
              </label>
            </div>
            
            <div v-if="!profileForm.phone && preferences.sms_notifications === false" class="notification-warning">
              Add a phone number above to enable SMS notifications
            </div>
          </section>

          <!-- Save Status -->
          <div v-if="saveStatus" :class="['save-status', saveStatus.type]">
            {{ saveStatus.message }}
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="close">Close</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { ref, watch, computed, inject, reactive } from 'vue'
import http from '@/api/http'

export default {
  name: 'ProfileSettingsModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const preferences = ref({
      email_notifications: false,
      sms_notifications: false
    })

    const profileForm = reactive({
      name: '',
      phone: '',
      timezone: 'America/Los_Angeles'
    })
    
    const originalProfile = ref({})
    const profileSaving = ref(false)

    // Inject theme state from App.vue
    const isDarkMode = inject('isDarkMode', ref(true))
    const isDarkModeLocal = ref(true)

    const loading = ref(false)
    const saveStatus = ref(null)

    // Format phone for display: +12065551234 -> (206) 555-1234
    const formatPhoneDisplay = (phone) => {
      if (!phone) return ''
      const digits = phone.replace(/\D/g, '')
      if (digits.length === 11 && digits.startsWith('1')) {
        const num = digits.slice(1)
        return '(' + num.slice(0,3) + ') ' + num.slice(3,6) + '-' + num.slice(6)
      }
      if (digits.length === 10) {
        return '(' + digits.slice(0,3) + ') ' + digits.slice(3,6) + '-' + digits.slice(6)
      }
      return phone
    }

    // Get user info from localStorage
    const userInfo = computed(() => {
      try {
        const stored = localStorage.getItem('auth.user')
        if (stored) {
          return JSON.parse(stored)
        }
      } catch (e) {
        console.error('Failed to parse user info')
      }
      return { name: '', email: '', roles: [] }
    })
    
    // Check if profile has changed
    const profileChanged = computed(() => {
      return profileForm.name !== originalProfile.value.name ||
             profileForm.phone !== originalProfile.value.phone ||
             profileForm.timezone !== originalProfile.value.timezone
    })

    const loadPreferences = async () => {
      try {
        loading.value = true
        const response = await http.get('/api/v1/preferences/me')
        preferences.value = {
          email_notifications: response.data.email_notifications || false,
          sms_notifications: response.data.sms_notifications || false
        }
        isDarkModeLocal.value = (response.data.theme || 'dark') === 'dark'
      } catch (error) {
        console.error('Failed to load preferences:', error)
      } finally {
        loading.value = false
      }
    }
    
    const loadProfile = async () => {
      try {
        const response = await http.get('/api/v1/users/me')
        profileForm.name = response.data.name || ''
        profileForm.phone = formatPhoneDisplay(response.data.phone) || ''
        profileForm.timezone = response.data.timezone || 'America/Los_Angeles'
        
        // Store original values for comparison
        originalProfile.value = {
          name: profileForm.name,
          phone: profileForm.phone,
          timezone: profileForm.timezone
        }
      } catch (error) {
        console.error('Failed to load profile:', error)
        // Fall back to localStorage data
        profileForm.name = userInfo.value.name || ''
        profileForm.phone = formatPhoneDisplay(userInfo.value.phone) || ''
        profileForm.timezone = userInfo.value.timezone || 'America/Los_Angeles'
        originalProfile.value = { ...profileForm }
      }
    }

    const savePreferences = async () => {
      try {
        await http.patch('/api/v1/preferences/me', {
          email_notifications: preferences.value.email_notifications,
          sms_notifications: preferences.value.sms_notifications
        })
        saveStatus.value = { type: 'success', message: 'Settings saved' }
        setTimeout(() => { saveStatus.value = null }, 2000)
      } catch (error) {
        console.error('Failed to save preferences:', error)
        saveStatus.value = { type: 'error', message: 'Failed to save' }
        setTimeout(() => { saveStatus.value = null }, 3000)
      }
    }
    
    const saveProfile = async () => {
      try {
        profileSaving.value = true
        const response = await http.patch('/api/v1/users/me', {
          name: profileForm.name || null,
          phone: profileForm.phone || null,
          timezone: profileForm.timezone || null
        })
        
        // Update localStorage with new values
        const storedUser = JSON.parse(localStorage.getItem('auth.user') || '{}')
        storedUser.name = response.data.name
        storedUser.phone = response.data.phone
        storedUser.timezone = response.data.timezone
        localStorage.setItem('auth.user', JSON.stringify(storedUser))
        
        // Update original values and reformat phone
        profileForm.phone = formatPhoneDisplay(response.data.phone)
        originalProfile.value = {
          name: response.data.name || '',
          phone: profileForm.phone,
          timezone: response.data.timezone || 'America/Los_Angeles'
        }
        
        saveStatus.value = { type: 'success', message: 'Profile updated' }
        setTimeout(() => { saveStatus.value = null }, 2000)
      } catch (error) {
        console.error('Failed to save profile:', error)
        const message = error.response?.data?.detail || 'Failed to save profile'
        saveStatus.value = { type: 'error', message }
        setTimeout(() => { saveStatus.value = null }, 3000)
      } finally {
        profileSaving.value = false
      }
    }

    const toggleTheme = async () => {
      try {
        isDarkMode.value = isDarkModeLocal.value
        localStorage.setItem('pam.theme', isDarkModeLocal.value ? 'dark' : 'light')
        await http.patch('/api/v1/preferences/me', {
          theme: isDarkModeLocal.value ? 'dark' : 'light'
        })
      } catch (error) {
        console.error('Failed to save theme:', error)
      }
    }

    const close = () => {
      emit('update:modelValue', false)
    }

    // Load preferences and profile when modal opens
    watch(() => props.modelValue, (newVal) => {
      if (newVal) {
        loadPreferences()
        loadProfile()
      }
    })

    return {
      preferences,
      profileForm,
      profileChanged,
      profileSaving,
      userInfo,
      loading,
      saveStatus,
      savePreferences,
      saveProfile,
      close,
      isDarkModeLocal,
      toggleTheme
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
}

.settings-modal {
  background: var(--bg-card, #1a1a2e);
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  border: 1px solid var(--border-color, #2d2d44);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border-color, #2d2d44);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-primary, #f0f0f0);
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.75rem;
  color: #888;
  cursor: pointer;
  padding: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--bg-secondary, #2d2d44);
  color: var(--text-primary, #f0f0f0);
}

.modal-body {
  padding: 1.5rem;
}

.settings-section {
  margin-bottom: 2rem;
}

.settings-section:last-child {
  margin-bottom: 0;
}

.settings-section h3 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin: 0 0 1rem 0;
}

.section-description {
  font-size: 0.875rem;
  color: var(--text-secondary, #888);
  margin: -0.5rem 0 1rem 0;
}

/* Profile Form */
.profile-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary, #aaa);
}

.form-group input,
.form-group select {
  padding: 0.75rem 1rem;
  background: var(--bg-secondary, #252540);
  border: 1px solid var(--border-color, #3d3d5c);
  border-radius: 8px;
  color: var(--text-primary, #f0f0f0);
  font-size: 0.9rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.2);
}

.form-group input.disabled-input {
  opacity: 0.6;
  cursor: not-allowed;
}

.field-hint {
  font-size: 0.75rem;
  color: #666;
}

.save-profile-btn {
  margin-top: 0.5rem;
  align-self: flex-start;
}

.btn-primary {
  background: #3498db;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #2980b9;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.roles-display {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: var(--bg-secondary, #252540);
  border-radius: 8px;
  font-size: 0.875rem;
}

.roles-label {
  color: var(--text-secondary, #888);
  margin-right: 0.5rem;
}

.roles-value {
  color: var(--text-primary, #f0f0f0);
  font-weight: 500;
}

/* Settings Row */
.setting-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: var(--bg-secondary, #252540);
  border-radius: 8px;
  margin-bottom: 0.75rem;
}

.setting-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.setting-label {
  font-weight: 500;
  color: var(--text-primary, #f0f0f0);
}

.setting-description {
  font-size: 0.8rem;
  color: var(--text-secondary, #888);
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #3d3d5c;
  border-radius: 28px;
  transition: 0.3s;
}

.toggle-slider.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.3s;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #27ae60;
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(22px);
}

.toggle-switch input:disabled + .toggle-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Notification warning */
.notification-warning {
  background: rgba(241, 196, 15, 0.15);
  border: 1px solid rgba(241, 196, 15, 0.3);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-top: 0.5rem;
  color: #f1c40f;
  font-size: 0.875rem;
}

/* Save Status */
.save-status {
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  margin-top: 1rem;
  text-align: center;
}

.save-status.success {
  background: rgba(39, 174, 96, 0.15);
  color: #27ae60;
  border: 1px solid rgba(39, 174, 96, 0.3);
}

.save-status.error {
  background: rgba(231, 76, 60, 0.15);
  color: #e74c3c;
  border: 1px solid rgba(231, 76, 60, 0.3);
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color, #2d2d44);
  display: flex;
  justify-content: flex-end;
}

.btn {
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-secondary {
  background: var(--bg-secondary, #2d2d44);
  color: var(--text-primary, #f0f0f0);
  border: 1px solid var(--border-color, #3d3d5c);
}

.btn-secondary:hover {
  background: #3d3d5c;
}

/* Mobile */
@media (max-width: 480px) {
  .settings-modal {
    max-height: 100vh;
    border-radius: 0;
  }

  .setting-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .toggle-switch {
    align-self: flex-end;
  }
}
</style>
